import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Mail, Send, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { apiRequest } from '@/lib/queryClient';
import { SEO } from "@/components/seo";

const contactSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  title: z.string().min(1, 'Please enter a title').max(200, 'Title must be less than 200 characters'),
  message: z.string().min(10, 'Message must be at least 10 characters').max(2000, 'Message must be less than 2000 characters'),
});

type ContactData = z.infer<typeof contactSchema>;

export default function ContactPage() {
  const [, setLocation] = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<ContactData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      email: '',
      title: '',
      message: '',
    },
  });

  const onSubmit = async (data: ContactData) => {
    setIsSubmitting(true);
    try {
      await apiRequest('POST', '/api/contact', data);
      
      toast({
        title: 'Message sent successfully!',
        description: 'Thank you for contacting us. We\'ll get back to you within 24 hours.',
      });
      
      // Reset form after successful submission
      form.reset();
      
      // Optionally redirect to home page after a delay
      setTimeout(() => {
        setLocation('/');
      }, 2000);
      
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to send message',
        description: error instanceof Error ? error.message : 'Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-white relative min-h-screen overflow-hidden">
      <SEO
        title="Contact Us | BounceBunny"
        description="Have a question about your training plan? Need help with your account? Get in touch with the BounceBunny team - we're here to help."
        canonical="https://bouncebunny.app/contact"
      />
      {/* Background orbs matching the login page design */}
      <div className="floating-orb absolute z-0 w-96 h-96 rounded-full blur-3xl -top-48 -left-48 animate-float-slow" />
      <div className="floating-orb absolute z-0 w-80 h-80 rounded-full blur-3xl top-1/3 -right-40 animate-float-reverse" />
      <div className="floating-orb absolute z-0 w-64 h-64 rounded-full blur-3xl bottom-1/4 left-1/4 animate-float-slow" />

      {/* Back button */}
      <Link href="/">
        <Button
          variant="ghost"
          className="absolute left-4 top-4 z-20 text-white/80 hover:text-white hover:bg-white/10 backdrop-blur-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>
      </Link>

      <div className="relative z-10 min-h-screen grid place-items-center p-4 sm:p-6 pt-16 sm:pt-0">
        <div className="w-full max-w-2xl mx-auto">
          <Card className="relative glass-effect floating-modal soft-glow gradient-outline border-white/10 shadow-2xl">
            {/* Accent bar on top */}
            <div className="pointer-events-none absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-apple-gray via-white to-apple-gray opacity-70 rounded-t-xl" />
            
            <CardHeader className="text-center pb-4 sm:pb-6">
              <div className="flex items-center justify-center mb-4">
                <div className="p-3 rounded-full bg-white/10 backdrop-blur-sm">
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
              </div>
              <CardTitle className="text-xl sm:text-2xl font-bold text-white">
                Contact Us
              </CardTitle>
              <CardDescription className="text-white/70 text-sm sm:text-base">
                Have a question or need help? We'd love to hear from you.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Your Email Address
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="your@email.com"
                            type="email"
                            {...field}
                            className="glass-input"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">
                          Subject
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="What's this about?"
                            {...field}
                            className="glass-input"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/90">
                          Message
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Tell us more about your question or feedback..."
                            rows={6}
                            {...field}
                            className="glass-input resize-none"
                          />
                        </FormControl>
                        <div className="flex justify-between items-center">
                          <FormMessage />
                          <span className="text-xs text-white/50">
                            {field.value?.length || 0}/2000
                          </span>
                        </div>
                      </FormItem>
                    )}
                  />
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-white text-black hover:bg-white/90 font-medium py-3 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </Form>
              
              <div className="text-center pt-4 border-t border-white/10">
                <p className="text-white/60 text-sm">
                  You can also reach us directly at{' '}
                  <a 
                    href="mailto:support@bouncebunny.app" 
                    className="text-white hover:text-white/80 underline"
                  >
                    support@bouncebunny.app
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}